import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

let manualApiKey: string = "";

export const setManualApiKey = (key: string) => {
  manualApiKey = key;
};

const getAI = () => {
  return new GoogleGenAI({ apiKey: manualApiKey || process.env.API_KEY });
};

export const generateCampaignSequence = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  
  const systemInstruction = `YOU ARE A WORLD-CLASS FASHION PHOTOGRAPHER AND ART DIRECTOR FOR VOGUE. 
  Your goal is to create a sequence of 8 distinct photographic concepts based on a PROVIDED REFERENCE IMAGE.
  
  STRICT RULES:
  1. ANALYZE the reference image for: 
     - OUTFIT (the exact clothing items, fabrics, and colors).
     - ENVIRONMENT (the specific location, background, and setting).
     - VIBE (the lighting style and overall photographic quality).
  2. GENERATE 8 UNIQUE POSES AND CAMERA ANGLES.
  3. EVERY CONCEPT MUST maintain the SAME outfit and SAME environment as the reference image.
  4. Use technical photographic language: 'Shot on Hasselblad H6D-100c', '85mm f/1.2 prime lens', 'Profoto Pro-11 studio lighting', 'Golden hour natural rim light', 'Deep contrast editorial color grade'.
  5. Focus on materiality: silk sheen, leather grain, glass reflections, and ultra-fine skin textures.
  
  Do not describe the person's facial features.
  Output a JSON ARRAY of 8 objects.`;

  const parts: any[] = [{ text: "Generate 8 hyper-realistic, high-fashion campaign concepts maintaining the exact outfit and location from this image. Ensure technical photographic excellence." }];
  if (brief.anchorImage) {
    const mime = brief.anchorImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.anchorImage.split(',')[1] } });
  }

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              title: { type: Type.STRING, description: "Short descriptive title of the pose/shot" },
              prompt: { type: Type.STRING, description: "Detailed technical photographic prompt including lighting and camera gear" },
            },
            required: ["id", "title", "prompt"],
          },
        },
      },
    });

    return JSON.parse(response.text?.trim() || "[]");
  } catch (error: any) {
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

export const generatePoseImage = async (
  brief: CreativeBrief,
  concept: CampaignConcept
): Promise<string> => {
  const ai = getAI();
  const model = 'gemini-3-pro-image-preview';
  
  if (!brief.anchorImage) throw new Error("No anchor image");

  const mime = brief.anchorImage.split(';')[0].split(':')[1] || 'image/jpeg';
  const parts: any[] = [
    { inlineData: { mimeType: mime, data: brief.anchorImage.split(',')[1] } }
  ];

  const finalPrompt = `
    MASTER REFERENCE: The attached photo provides the IDENTITY (face), OUTFIT, and ENVIRONMENT.
    TASK: Generate a new photograph of THIS EXACT SAME PERSON, wearing the SAME OUTFIT, in the SAME LOCATION.
    SCENE DIRECTIVE: ${concept.prompt}.
    MANDATORY QUALITY STANDARDS:
    - Camera: Hasselblad H6D-100c, 100MP Medium Format.
    - Lighting: Cinematic, three-point lighting with soft-box diffusion and precise hair-light.
    - Consistency: 100% match on attire textures (silk, wool, cotton) and background geometry.
    - Aesthetics: Sharp focus on the subject, natural bokeh, professional color science, ultra-high dynamic range (HDR).
    - Details: Photorealistic skin pores, individual hair strands, uncompressed raw image look.
    
    OUTPUT: A high-end editorial masterpiece.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config: {
        imageConfig: {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize
        }
      },
    });

    const imagePart = response.candidates?.[0]?.content?.parts?.find(p => !!p.inlineData);
    if (imagePart?.inlineData) return `data:image/png;base64,${imagePart.inlineData.data}`;
    throw new Error("PRODUCTION_ERROR");
  } catch (error: any) {
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};